import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Link2, Linkedin, MessageCircle, Twitter, Instagram, Youtube } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { SITE_URL } from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import { PostCard } from "@/pages/Blog";
import { BlogBlock, getHeadings } from "@/blog/BlogBlocks";
import { markPrerenderReady } from "@/lib/seo";
import { trackEvent } from "@/blog/track";
import {
  AUTHOR,
  getPost,
  getRelatedPosts,
  readingMinutes,
  formatDate,
} from "@/blog/posts";

/* ---------- Reading progress: answers the reader's own scrolling ---------- */
function ReadingProgress({ targetRef }) {
  const [p, setP] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = targetRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const done = Math.min(Math.max(-rect.top / Math.max(total, 1), 0), 1);
      setP(done);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [targetRef]);

  return (
    <div className="pointer-events-none fixed left-0 top-0 z-[60] h-1 w-full" aria-hidden="true">
      <div
        className="h-full origin-left bg-sunset-gradient"
        style={{ transform: `scaleX(${p})` }}
      />
    </div>
  );
}

/* ---------- "On this page" ---------- */
function Toc({ headings }) {
  const [active, setActive] = useState(headings[0]?.id);

  useEffect(() => {
    const els = headings.map((h) => document.getElementById(h.id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav aria-label="On this page" className="sticky top-28" data-testid="blog-toc">
      <p className="text-sm font-bold text-ink">On this page</p>
      <ul className="mt-3 space-y-1 border-l border-ink/10">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              className={`-ml-px block border-l-2 py-1.5 pl-4 text-[13.5px] leading-snug transition-colors ${
                active === h.id
                  ? "border-hotpink font-semibold text-ink"
                  : "border-transparent text-ink/55 hover:text-ink"
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ---------- Share ---------- */
function ShareRow({ url, title, slug }) {
  const enc = encodeURIComponent;
  const btn =
    "grid h-10 w-10 place-items-center rounded-full border border-ink/15 bg-white text-ink/70 transition-colors hover:border-hotpink hover:text-hotpink";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      trackEvent("blog_share", { article: slug, method: "copy_link" });
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="blog-share">
      <p className="mr-1 text-sm font-semibold text-ink">Share this article</p>
      <a
        className={btn}
        aria-label="Share on LinkedIn"
        onClick={() => trackEvent("blog_share", { article: slug, method: "linkedin" })}
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Linkedin size={16} />
      </a>
      <a
        className={btn}
        aria-label="Share on X"
        onClick={() => trackEvent("blog_share", { article: slug, method: "x" })}
        href={`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Twitter size={16} />
      </a>
      <a
        className={btn}
        aria-label="Share on WhatsApp"
        onClick={() => trackEvent("blog_share", { article: slug, method: "whatsapp" })}
        href={`https://wa.me/?text=${enc(`${title} ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <MessageCircle size={16} />
      </a>
      <button type="button" className={btn} aria-label="Copy link" onClick={copy}>
        <Link2 size={16} />
      </button>
    </div>
  );
}

/* ---------- Page ---------- */
export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const articleRef = useRef(null);

  useEffect(() => {
    if (post) markPrerenderReady();
  }, [post]);

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Article not found | Checkout DM</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="prerender-status-code" content="404" />
        </Helmet>
        <NotFound />
      </>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const image = `${SITE_URL}${post.cover.src}`;
  const headings = getHeadings(post.blocks);
  const tocHeadings = post.faqs?.length
    ? [...headings, { id: "faq", text: "Frequently asked questions" }]
    : headings;
  const related = getRelatedPosts(post, 2);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: [image],
      datePublished: post.date,
      dateModified: post.updated || post.date,
      articleSection: post.category,
      keywords: post.tags.join(", "),
      author: { "@type": "Person", name: AUTHOR.name, url: AUTHOR.url },
      publisher: {
        "@type": "Organization",
        name: "Checkout DM",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
  ];

  if (post.faqs?.length) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <>
      <SEO
        title={`${post.seoTitle || post.title} | Checkout DM`}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={image}
        type="article"
        structuredData={schema}
      />
      <Helmet>
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.updated || post.date} />
        <meta property="article:author" content={AUTHOR.name} />
        <meta property="article:section" content={post.category} />
        {post.tags.map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
      </Helmet>

      <ReadingProgress targetRef={articleRef} />
      <Navbar />

      <header className="px-6 pb-8 pt-12 md:px-12 md:pt-20">
        <div className="mx-auto max-w-5xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 transition-colors hover:text-hotpink"
          >
            <ArrowLeft size={16} /> All articles
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink/55">
            <span className="rounded-full bg-lilac px-3 py-1 text-xs font-bold text-ink">
              {post.category}
            </span>
            <span>{formatDate(post.date)}</span>
            <span>{readingMinutes(post)} min read</span>
          </div>

          <h1
            data-testid="blog-post-title"
            className="mt-5 max-w-4xl text-balance font-display text-4xl leading-[0.98] tracking-tighter md:text-6xl"
          >
            {post.title}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/70">{post.excerpt}</p>

          <div className="mt-7 flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-ink font-display text-sm font-black text-cream">
              {AUTHOR.initials}
            </span>
            <div className="leading-tight">
              <a
                href={AUTHOR.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-ink hover:text-hotpink"
              >
                {AUTHOR.name}
              </a>
              <p className="text-[13px] text-ink/55">{AUTHOR.role}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12">
        <figure className="mx-auto max-w-5xl">
          <img
            src={post.cover.src}
            alt={post.cover.alt}
            width="1200"
            height="630"
            fetchPriority="high"
            className="w-full rounded-[2rem] border border-ink/10"
          />
        </figure>
      </div>

      <div className="px-6 pb-24 pt-6 md:px-12">
        <div className="mx-auto grid max-w-5xl gap-14 lg:grid-cols-[minmax(0,680px)_1fr]">
          <article ref={articleRef} data-testid="blog-article" className="min-w-0">
            {post.blocks.map((block, i) => (
              <BlogBlock key={i} block={block} />
            ))}

            {post.related?.length > 0 && (
              <section className="mt-14" aria-labelledby="related-reading" data-testid="blog-related">
                <h2 id="related-reading" className="font-display text-2xl leading-tight tracking-tight">
                  Related reading
                </h2>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {post.related.map((r) => (
                    <li key={r.to}>
                      <Link
                        to={r.to}
                        onClick={() => trackEvent("blog_related_click", { article: post.slug, destination: r.to })}
                        className="group flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-5 transition-colors hover:border-hotpink/50"
                      >
                        <span className="flex items-center justify-between gap-3 font-display text-[17px] font-bold text-ink">
                          {r.title}
                          <ArrowUpRight size={18} className="flex-none text-ink/40 transition-colors group-hover:text-hotpink" aria-hidden="true" />
                        </span>
                        <span className="mt-1.5 text-[14px] leading-relaxed text-ink/60">{r.description}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {post.faqs?.length > 0 && (
              <section className="mt-16" aria-labelledby="faq">
                <h2
                  id="faq"
                  className="scroll-mt-28 font-display text-[1.75rem] leading-[1.1] tracking-tight md:text-[2.1rem]"
                >
                  Frequently asked questions
                </h2>
                <div className="mt-6 divide-y divide-ink/10 rounded-3xl border border-ink/10 bg-white">
                  {post.faqs.map((f) => (
                    <details key={f.q} className="group px-6 py-5">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-bold text-ink">
                        {f.q}
                        <span
                          aria-hidden="true"
                          className="text-2xl leading-none text-hotpink transition-transform group-open:rotate-45"
                        >
                          +
                        </span>
                      </summary>
                      <p className="mt-3 text-[16px] leading-relaxed text-ink/70">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}

            <aside className="mt-16 rounded-[2rem] bg-ink p-8 text-cream md:p-10" data-testid="blog-cta">
              <h2 className="font-display text-3xl leading-[1.05] tracking-tight md:text-4xl">
                Turn your DMs into checkouts.
              </h2>
              <p className="mt-3 max-w-md text-[16px] leading-relaxed text-cream/70">
                Connect your Instagram account and set up your catalog in minutes. The Free plan
                includes 5 products and 15 customer conversations a month.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link
                  to="/login"
                  onClick={() => trackEvent("blog_cta_click", { article: post.slug, cta_location: "article_end", destination: "/login" })}
                  className="cta-glow rounded-full bg-sunset-gradient px-6 py-3 text-sm font-semibold text-white"
                >
                  Connect Instagram
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => trackEvent("blog_cta_click", { article: post.slug, cta_location: "article_end", destination: "/pricing" })}
                  className="text-sm font-semibold text-cream/80 hover:text-white"
                >
                  See pricing
                </Link>
              </div>
            </aside>

            <div className="mt-10 border-t border-ink/10 pt-8">
              <ShareRow url={url} title={post.title} slug={post.slug} />
            </div>

            <div className="mt-10 flex gap-4 rounded-3xl border border-ink/10 bg-white p-6">
              <span className="grid h-12 w-12 flex-none place-items-center rounded-full bg-ink font-display text-sm font-black text-cream">
                {AUTHOR.initials}
              </span>
              <div>
                <p className="font-display text-lg font-black text-ink">{AUTHOR.name}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-ink/70">
                  Founder of Checkout DM, building DM-based checkout for Instagram sellers. He also
                  shares AI automation tutorials on Instagram and YouTube.
                </p>
                <div className="mt-3 flex items-center gap-3 text-ink/60">
                  <a href={AUTHOR.url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-hotpink">
                    <Linkedin size={18} />
                  </a>
                  <a href="https://instagram.com/princejindalai" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-hotpink">
                    <Instagram size={18} />
                  </a>
                  <a href="https://youtube.com/@PrinceJindalAI" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-hotpink">
                    <Youtube size={18} />
                  </a>
                </div>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <Toc headings={tocHeadings} />
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mx-auto mt-20 max-w-5xl" aria-labelledby="keep-reading">
            <h2 id="keep-reading" className="font-display text-3xl tracking-tight">
              Keep reading
            </h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              {related.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </>
  );
}
